/**
 * BenchmarkSitesPage.tsx — Peer-reviewed shallow-lake datasets that match
 *                          Barcombe's physical profile. Each card shows the
 *                          measured inputs + observed outcomes + citations,
 *                          and an "Import to Train Summary" button that pushes
 *                          the parameters into sessionStorage for the
 *                          /train-summary/:id page to consume on mount.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Database, ArrowLeft, ExternalLink, BookOpen, MapPin, Beaker,
  CheckCircle2, AlertTriangle, Download, Workflow, Map as MapIcon,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { BENCHMARK_SITES, TRAIN_SCENARIO_KEY, type BenchmarkSite } from '@/lib/benchmarkSites';
import { MODULE_TRAINS } from '@/lib/moduleTrains';
import BathymetryMap, { type PlacedModule } from '@/components/BathymetryMap';

export default function BenchmarkSitesPage() {
  const navigate = useNavigate();
  const [importedId, setImportedId] = useState<string | null>(null);

  const importScenario = (site: BenchmarkSite, trainId: string) => {
    try {
      sessionStorage.setItem(TRAIN_SCENARIO_KEY, JSON.stringify({
        siteId: site.id, siteName: site.name, inputs: site.inputs, timestamp: Date.now(),
      }));
      setImportedId(site.id);
      // Navigate to the train summary using the imported scenario
      navigate(`/train-summary/${trainId}`);
    } catch (e) {
      console.error('Failed to persist scenario:', e);
    }
  };

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={Database} title="Benchmark Sites — Peer-Reviewed Shallow-Lake Datasets"
        subtitle="Import measured parameters from Barcombe-equivalent reservoirs to validate which module-trains reproduce observed behaviour"
        actions={
          <button onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        }
      />

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <strong className="text-slate-200">How to use:</strong> Each card below is a shallow polymictic lake with comprehensive
        published monitoring data (UKCEH NERC EIDC, IGB Berlin LTER, PANGAEA, peer-reviewed primary papers). Inputs are the
        site's measured physical/chemical parameters; outcomes are the corresponding measured chl-a peaks, anoxia onsets,
        SOD values, and breach events. Click <em>Import &amp; analyse</em> next to a recommended train to push the site's
        parameters into the Train Summary page — the simulator will then predict outcomes which you can compare against
        the published observations to see which modules give the closest fit.
      </div>

      <div className="grid grid-cols-1 gap-4">
        {BENCHMARK_SITES.map(site => (
          <SiteCard key={site.id} site={site} onImport={importScenario} imported={importedId === site.id} />
        ))}
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-rb-brand" /> Why these four sites?
        </div>
        <ul className="list-disc ml-5 space-y-1.5">
          <li><strong className="text-slate-200">Loch Leven</strong> — Closest UK climate match; 50+ year continuous time series; documented eutrophication recovery provides before/after for capping/aeration analogues.</li>
          <li><strong className="text-slate-200">Lake Müggelsee</strong> — Best-instrumented shallow eutrophic lake in Europe; IGB has microelectrode SOD and porewater P that map directly to Boudreau + NOM-SOD modules.</li>
          <li><strong className="text-slate-200">Esthwaite Water</strong> — Smallest of the four (1 km²) — closest to Barcombe size class; stratifies more strongly so it validates Davison Fe-Mn and Wüest plume modules.</li>
          <li><strong className="text-slate-200">Bautzen Reservoir</strong> — Documented aeration trial — direct evidence of OTE, stratification breakdown timing and Microcystis suppression under real aeration.</li>
        </ul>
        <p className="mt-3">Each cited paper is open-access or available via institutional subscription; DOIs and repository URLs are quoted verbatim from the original sources.</p>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────

function SiteCard({ site, onImport, imported }: {
  site: BenchmarkSite;
  onImport: (s: BenchmarkSite, trainId: string) => void;
  imported: boolean;
}) {
  return (
    <section className={`rounded-2xl border-2 p-5 ${imported ? 'border-emerald-700 bg-emerald-900/10' : 'border-slate-700 bg-slate-900'}`}>
      <div className="flex flex-col md:flex-row justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <MapPin className="w-5 h-5 text-sky-300" />
            <h2 className="text-xl font-bold text-slate-100">{site.name}</h2>
            <span className="text-xs text-slate-400">· {site.country}</span>
            {imported && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-900/40 text-emerald-200 text-xs font-semibold px-2 py-0.5"><CheckCircle2 className="w-3 h-3" /> IMPORTED</span>}
          </div>
          <p className="text-xs text-slate-400 mt-2 leading-relaxed">{site.similarityRationale}</p>
        </div>
      </div>

      {/* Three-column layout: inputs / outcomes / refs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-4">
        {/* Inputs */}
        <div className="rounded-xl border border-slate-700 bg-slate-950/50 p-3">
          <div className="text-xs uppercase tracking-wider text-sky-300 font-semibold mb-2 flex items-center gap-1">
            <Beaker className="w-3.5 h-3.5" /> Measured Inputs
          </div>
          <div className="text-xs text-slate-300 space-y-0.5">
            <Row k="Area" v={`${(site.inputs.area_m2 / 1e6).toFixed(2)} km²`} />
            <Row k="Mean depth" v={`${site.inputs.depth_mean_m} m`} />
            <Row k="Max depth" v={`${site.inputs.depth_max_m} m`} />
            <Row k="Chl-a (summer mean)" v={`${site.inputs.chl_ugL} µg/L`} />
            <Row k="Chl-a peak" v={`${site.inputs.chl_peak_ugL} µg/L`} />
            <Row k="DOC / NOM" v={`${site.inputs.nom_mgL} mg/L`} />
            <Row k="TN / TP" v={`${site.inputs.tn_mgL} / ${(site.inputs.tp_mgL * 1000).toFixed(0)} mg-µg/L`} />
            <Row k="pH" v={site.inputs.pH.toFixed(1)} />
            <Row k="Surface T" v={`${site.inputs.surfaceTempC} °C`} />
            <Row k="Hypolimnion DO" v={`${site.inputs.doHypo_mgL} mg/L`} />
            <Row k="Stratified" v={site.inputs.stratified ? 'yes' : 'polymictic'} />
            <Row k="Cyano fraction" v={`${(site.inputs.fCyano * 100).toFixed(0)} %`} />
            <Row k="Wind (10 m)" v={`${site.inputs.windMs} m/s`} />
            <Row k="Mn(II)" v={`${(site.inputs.mnII_mgL * 1000).toFixed(0)} µg/L`} />
            <Row k="ORP" v={`${site.inputs.orp_mV} mV`} />
          </div>
        </div>

        {/* Outcomes */}
        <div className="rounded-xl border border-emerald-700/50 bg-emerald-900/10 p-3">
          <div className="text-xs uppercase tracking-wider text-emerald-300 font-semibold mb-2 flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" /> Observed Outcomes (to match)
          </div>
          <ul className="space-y-1.5 text-xs">
            {site.outcomes.map((o, i) => (
              <li key={i} className="border-l-2 border-emerald-700 pl-2">
                <div className="text-slate-300">{o.metric}</div>
                <div className="text-emerald-200 font-mono font-semibold">{o.value}</div>
                <div className="text-[10px] text-slate-500 italic">{o.source}</div>
              </li>
            ))}
          </ul>
        </div>

        {/* References */}
        <div className="rounded-xl border border-slate-700 bg-slate-950/50 p-3">
          <div className="text-xs uppercase tracking-wider text-fuchsia-300 font-semibold mb-2 flex items-center gap-1">
            <BookOpen className="w-3.5 h-3.5" /> Citations / Open Data
          </div>
          <ul className="space-y-2 text-xs">
            {site.references.map((r, i) => (
              <li key={i}>
                <div className="text-slate-300 leading-snug">{r.citation}</div>
                {(r.doi || r.url) && (
                  <a href={r.doi ? `https://doi.org/${r.doi}` : r.url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] text-sky-300 hover:text-sky-200 mt-0.5">
                    {r.doi ? `doi:${r.doi}` : 'open data'} <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Caveats */}
      <div className="mt-3 rounded-lg border border-amber-700/50 bg-amber-900/10 p-2.5 text-[11px] text-amber-100/90">
        <div className="font-semibold mb-0.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Caveats:</div>
        {site.caveats}
      </div>

      {/* Bathymetry map */}
      <div className="mt-3 rounded-xl border border-sky-700/50 bg-slate-950/40 p-3">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="text-xs font-semibold text-sky-200 flex items-center gap-1.5">
            <MapIcon className="w-4 h-4" /> Bathymetric map — place aeration modules
          </div>
          <div className="text-[11px] text-slate-500 italic">
            Source: {site.bathymetry.source}
            {site.bathymetry.sourceUrl && (
              <a href={site.bathymetry.sourceUrl} target="_blank" rel="noopener noreferrer"
                className="ml-1 text-sky-400 hover:text-sky-300 inline-flex items-center gap-0.5">
                ↗
              </a>
            )}
          </div>
        </div>
        <BathymetryMap
          bathymetry={site.bathymetry}
          depthMax={site.inputs.depth_max_m}
          siteName={site.name}
          width={720}
        />
      </div>

      {/* Train-import row */}
      <div className="mt-3 border-t border-slate-700 pt-3">
        <div className="text-xs font-semibold text-slate-300 mb-2 flex items-center gap-1">
          <Workflow className="w-3.5 h-3.5 text-sky-300" />
          Recommended trains for this site — click to import &amp; analyse:
        </div>
        <div className="flex flex-wrap gap-2">
          {site.recommendedTrains.map(tid => {
            const train = MODULE_TRAINS.find(t => t.id === tid);
            if (!train) return null;
            return (
              <button key={tid} onClick={() => onImport(site, tid)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 px-3 py-1.5 text-xs font-semibold text-white">
                <Download className="w-3.5 h-3.5" />
                Import → {train.label.split(' · ')[1] || train.label}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between border-b border-slate-800/50 py-0.5">
      <span className="text-slate-500">{k}</span>
      <span className="text-slate-200 font-mono">{v}</span>
    </div>
  );
}
